export type FileChatActivityKind = "reasoning" | "tool";
export type FileChatActivityStatus = "active" | "complete" | "failed";

export interface FileChatActivity {
  id: string;
  kind: FileChatActivityKind;
  label: string;
  detail?: string;
  status: FileChatActivityStatus;
}

export type FileChatEvent =
  | { type: "status"; label: string }
  | { type: "activity"; activity: FileChatActivity }
  | { type: "delta"; delta: string }
  | { type: "error"; message: string };

export function parseFileChatEvent(line: string): FileChatEvent {
  const value = JSON.parse(line) as unknown;
  if (!value || typeof value !== "object") throw new Error("Malformed chat event.");
  const event = value as Record<string, unknown>;
  if (event.type === "status" && typeof event.label === "string") {
    return { type: "status", label: event.label };
  }
  if (event.type === "delta" && typeof event.delta === "string") {
    return { type: "delta", delta: event.delta };
  }
  if (event.type === "error" && typeof event.message === "string") {
    return { type: "error", message: event.message };
  }
  if (event.type === "activity" && event.activity && typeof event.activity === "object") {
    const activity = event.activity as Record<string, unknown>;
    if (
      typeof activity.id === "string"
      && (activity.kind === "reasoning" || activity.kind === "tool")
      && typeof activity.label === "string"
      && (activity.status === "active" || activity.status === "complete" || activity.status === "failed")
      && (activity.detail === undefined || typeof activity.detail === "string")
    ) {
      return {
        type: "activity",
        activity: {
          id: activity.id,
          kind: activity.kind,
          label: activity.label,
          status: activity.status,
          ...(typeof activity.detail === "string" ? { detail: activity.detail } : {}),
        },
      };
    }
  }
  throw new Error("Unknown chat event.");
}

/**
 * Identity of the reviewed commit, sent alongside the chat so the local
 * companion can resolve a real checkout instead of reasoning from the diff.
 */
export interface FileChatSource {
  repository: string;
  headSha: string;
  headRef?: string;
  pullNumber?: number;
}

const REPOSITORY_PATTERN = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;
const COMMIT_PATTERN = /^[0-9a-f]{7,64}$/;
const REF_PATTERN = /^[A-Za-z0-9._][A-Za-z0-9._/-]*$/;

/** Refs and slugs become git arguments, so reject anything git could misread. */
function safeRef(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const ref = value.trim();
  if (!ref || ref.length > 255 || ref.includes("..") || ref.endsWith("/") || !REF_PATTERN.test(ref)) return undefined;
  return ref;
}

export function parseFileChatSource(value: unknown): FileChatSource | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "object") throw new Error("Malformed review source.");
  const source = value as Record<string, unknown>;
  const repository = typeof source.repository === "string" ? source.repository.trim() : "";
  const headSha = typeof source.headSha === "string" ? source.headSha.trim().toLowerCase() : "";
  if (!REPOSITORY_PATTERN.test(repository)) throw new Error("Malformed review source repository.");
  if (!COMMIT_PATTERN.test(headSha)) throw new Error("Malformed review source head commit.");
  const pullNumber = typeof source.pullNumber === "number" && Number.isSafeInteger(source.pullNumber) && source.pullNumber > 0
    ? source.pullNumber
    : undefined;
  return {
    repository,
    headSha,
    ...(safeRef(source.headRef) ? { headRef: safeRef(source.headRef) } : {}),
    ...(pullNumber ? { pullNumber } : {}),
  };
}

/**
 * How much of the checkout the assistant may touch. `read-only` reads files and
 * nothing else; `workspace` lets it edit and run commands inside the checkout,
 * which is a disposable detached worktree rather than the reviewer's own tree.
 */
export const fileChatModes = ["read-only", "workspace"] as const;
export type FileChatMode = (typeof fileChatModes)[number];

export function isFileChatMode(value: unknown): value is FileChatMode {
  return typeof value === "string" && (fileChatModes as readonly string[]).includes(value);
}

/** Absent means read-only, so a companion never widens access for an older client. */
export function parseFileChatMode(value: unknown): FileChatMode {
  if (value === undefined || value === null) return "read-only";
  if (!isFileChatMode(value)) throw new Error("Unknown chat mode.");
  return value;
}
